import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { isPublicWebAppBuild } from './@shared/is-public-web-app';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AppMigrationService } from 'src/app/@core/app-migration.service';
import { C } from './@shared/constants';
import { Capacitor } from '@capacitor/core';
import { DeeplinkService } from 'src/app/@core/deeplink.service';
import { Events } from 'src/app/@core/event.service';
import { LanguageService } from 'src/app/@core/language.service';
import { LocalNotificationService } from 'src/app/@core/local-notification.service';
import { NetworkService } from 'src/app/@core/network.service';
import { Platform } from '@ionic/angular';
import { PwaService } from 'src/app/@core/pwa.service';
import { RatingService } from './@core/rating.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { TrackingService } from 'src/app/@core/tracking.service';
import { environment } from '../environments/environment';
import { register } from 'swiper/element/bundle';

register();

declare let window: Window;

type PROTO_FEATURES = 'rating' | 'pwa' | 'localNotifications';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
})
export class AppComponent {
  @ViewChild('publicScrollRegion') private publicScrollRegion?: ElementRef<HTMLElement>;

  public readonly isPublicWebApp = isPublicWebAppBuild();
  private readonly router = inject(Router);

  private features: { [key in PROTO_FEATURES]?: boolean } = {
    localNotifications: false,
    pwa: true,
    rating: false,
  };

  // eslint-disable-next-line max-params
  constructor(
    private appMigrationService: AppMigrationService,
    private deeplinkService: DeeplinkService,
    private events: Events,
    private languageService: LanguageService,
    private localNotificationService: LocalNotificationService,
    private platform: Platform,
    private pwaService: PwaService,
    private networkService: NetworkService,
    private trackingService: TrackingService,
    private ratingService: RatingService,
  ) {
    if (this.isPublicWebApp) {
      this.router.events.pipe(filter((event) => { return event instanceof NavigationEnd; }),
        takeUntilDestroyed()).subscribe(() => {
        this.publicScrollRegion?.nativeElement.scrollTo(0, 0);
      });
    }

    this.init().catch((error) => {
      console.error(error.message);
    });
  }

  public get isHomeRoute(): boolean {
    return this.router.isActive('/home', {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  public ngOnDestroy(): void {
    this.events.unsubscribe('status-bar:update');
  }

  public async init(): Promise<void> {
    this.handleConsole();

    await this.platform.ready();
    this.observeStatusBarStyle();

    await this.appMigrationService.init();
    await this.languageService.init();
    await this.deeplinkService.init();

    if (this.features.localNotifications) {
      await this.localNotificationService.init();
    }

    this.networkService.init();
    this.trackingService.init();

    if (this.features.rating) {
      this.ratingService.showRating();
    }

    if (this.features.pwa) {
      this.pwaService.init();
    } else {
      console.warn('mobile only app -> pwa service skipped');
    }

    // provide extra css class for low end android devices
    if (C.isLowEndDevice()) {
      document.documentElement.classList.add('low-end');
    }
  }

  private handleConsole(): void {
    if (window.localStorage.getItem('PROTO_DEBUG')) { return; }

    let methods: string[] = [];

    switch (environment.name) {
      case 'production':
      case 'production-public': // eslint-disable-line
      case 'staging': // eslint-disable-line
      case 'staging-public': // eslint-disable-line
        methods = ['log', 'debug', 'info', 'warn'];
        break;

      default:
        break;
    }

    if (methods.length) {
      console.warn('=====================================================');
      console.warn(' ');
      console.warn(`    🚨 Console output is disabled on ${environment.name}!`);
      console.warn(' ');
      console.warn('=====================================================');
    }

    methods.forEach((method) => {
      // eslint-disable-next-line no-console
      console[method] = (): void => { };
    });
  }

  private observeStatusBarStyle(): void {
    // android always have consistent status bar style -> no update needed (see status bar decorator)
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'android') {
      return;
    }

    this.events.subscribe('status-bar:update', (data) => {
      if (data.style) {
        StatusBar.setStyle({
          style: Style[data.style],
        }).catch();
      }
    });
  }
}
